import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send } from 'lucide-react';
import axios from 'axios';
import { getSiteSettings } from '@/lib/siteSettings';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [settings, setSettings] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await getSiteSettings();
        setSettings(res.data);
        if (res.data?.chatbot_welcome_message) {
          setMessages([{ text: res.data.chatbot_welcome_message, isBot: true, timestamp: new Date() }]);
        }
      } catch (e) { console.error(e); }
    };
    fetchSettings();
  }, []);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async () => {
    if (!inputValue.trim()) return;
    const userMsg = { text: inputValue, isBot: false, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    setIsTyping(true);

    try {
      const res = await axios.post(`${API}/chatbot/query`, { query: inputValue });
      setTimeout(() => {
        setMessages(prev => [...prev, { text: res.data.answer, isBot: true, timestamp: new Date(), matched: res.data.matched }]);
        setIsTyping(false);
      }, 400);
    } catch {
      setMessages(prev => [...prev, { text: "Sorry, I'm having trouble. Please use the contact form.", isBot: true, timestamp: new Date() }]);
      setIsTyping(false);
    }
  };

  if (!settings?.chatbot_enabled) return null;

  return (
    <>
      {/* Toggle Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-14 h-14 sm:w-16 sm:h-16 bg-accent text-white rounded-full shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center"
            data-testid="chatbot-open-button"
          >
            <MessageCircle size={24} />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed z-50 bg-card rounded-lg shadow-2xl flex flex-col overflow-hidden border border-border
              bottom-3 right-3 left-3 h-[calc(100vh-5rem)]
              sm:bottom-6 sm:right-6 sm:left-auto sm:w-96 sm:h-[550px]"
            data-testid="chatbot-window"
          >
            {/* Header */}
            <div className="bg-accent text-white p-3 sm:p-4 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
                  <MessageCircle size={18} />
                </div>
                <div>
                  <h3 className="font-sans font-semibold text-sm">Art Assistant</h3>
                  <p className="text-[10px] text-white/80">Ask me anything!</p>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 rounded-full p-1.5 transition-colors" data-testid="chatbot-close-button">
                <X size={20} />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 bg-background">
              {messages.map((msg, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.isBot ? 'justify-start' : 'justify-end'}`}>
                  <div className={`max-w-[85%] rounded-lg px-3 py-2 ${msg.isBot ? 'bg-card border border-border' : 'bg-accent text-white'}`}>
                    <p className="font-sans text-sm leading-relaxed whitespace-pre-wrap">{msg.text}</p>
                    <span className="text-[10px] opacity-60 mt-0.5 block">
                      {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </motion.div>
              ))}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-card border border-border rounded-lg px-4 py-3">
                    <div className="flex space-x-1.5">
                      {[0, 150, 300].map(d => (
                        <div key={d} className="w-2 h-2 bg-accent rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-border bg-card flex-shrink-0">
              <div className="flex gap-2">
                <input
                  type="text" value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }}}
                  placeholder="Ask a question..."
                  className="flex-1 px-3 py-2 bg-background border border-border rounded-lg font-sans text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                  data-testid="chatbot-input"
                />
                <button onClick={handleSend} disabled={!inputValue.trim()}
                  className="px-3 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50"
                  data-testid="chatbot-send-button">
                  <Send size={16} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Chatbot;
